/* The 2D contour chart's slice of the shared XYZ source data.
 *
 * The same gridding requirement as the surface - three distinct positions, because both
 * triangulate - so the only thing that differs from `buildSurfaceSourceData` is which settings
 * type it carries and that there is no camera to name an axis for. */

import type { FormattingAndLabelRules } from '@ardoq/data-model';
import type { FilterColorData } from '@ardoq/scope-data';
import type { PlottedContext } from './plottedContext';
import type { PermissionContext } from '@ardoq/access-control';
import { type Locale } from '@ardoq/locale';
import { type I18nLocale } from '@ardoq/i18n';
import type { LoadedGraphWithViewpointMode } from '@ardoq/graph';
import {
  type XyzFieldSelection,
  type RequiredDimension,
  type SurfaceColoring,
  type XyzDataPoint,
  type HorizontalAxes,
  toNiceHorizontalAxes,
} from '@ardoq/xyz-charts';
import type { ChartEmptyReason, FieldOption } from 'tabview/chartShared/types';
import {
  toFieldColoringLegendData,
  toSurfaceColoring,
  type FieldColoringLegendData,
} from './surfaceColoring';
import {
  FIELD_CHART_FIELD_TYPES,
  toListBinCounts,
  withCategoryAxes,
  type ListAxes,
  type ListBinCounts,
} from './listFields';
import { toFieldLabel } from 'tabview/chartShared/fieldLabel';
import { buildXyzPoints } from './buildXyzPoints';

/** Three is the floor for a triangulation with any area, as for the surface. */
const CONTOUR_MINIMUM_POSITIONS = 3;

export type ContourSourceDataArgs = {
  context: PlottedContext;
  /**
   * The only part of the settings that can move a point.
   *
   * Narrow for the reason `Surface3DGriddingSelection` is, and narrower than it: this builder does
   * no gridding, so not even `interpolation` reaches it - `buildXyzPoints` hands these five names
   * to `resolveXyzFields` and reads nothing else. The layer memoises the derivation, and keying
   * that memo on the whole settings object re-derived every point for a ramp, a band count or an
   * isoline toggle: a pass over every component in the traversal, and then a new `points` array,
   * which `ContourChart` answers with a re-grid. Typing it this way is what keeps it true - the
   * builder cannot read a setting it has not been given.
   */
  viewSettings: XyzFieldSelection;
  loadedGraph: LoadedGraphWithViewpointMode;
  permissionContext: PermissionContext;
  locale: Locale;
  i18nLocale: I18nLocale;
  formattingAndLabelRules: FormattingAndLabelRules;
  filterColorData: FilterColorData;
};

export type ContourSourceData = {
  viewInstanceId: string;
  points: XyzDataPoint[];
  /**
   * The plane the chart is drawn against, decided here rather than in the chart.
   *
   * Rounded outward to the numbers a reader would name, per axis. The rest of the derivation
   * lives in this layer, and the four XYZ views can only be made to agree about their shared
   * plane if one place decides it.
   */
  axes: HorizontalAxes;
  excludedByDimension: Record<RequiredDimension, number>;
  excludedCount: number;
  axisFields: FieldOption[];
  xAxisTitle: string;
  yAxisTitle: string;
  /** What the color means, for the legend and the tooltip. */
  valueTitle: string;
  isViewEmpty: boolean;
  emptyReason: ChartEmptyReason;
  locale: Locale;
  i18nLocale: I18nLocale;
  /** What colors the bands: the conditional formatting on the components, else the value. */
  coloring: SurfaceColoring;
  legend: FieldColoringLegendData;
  /** The list values on each axis plotting a list field. */
  listAxes: ListAxes;
  /** Bins along each list axis under `binned`, one per value across those plotted. */
  listBinCounts: ListBinCounts;
};

export const buildContourSourceData = (
  viewInstanceId: string,
  {
    context,
    viewSettings,
    loadedGraph,
    permissionContext,
    locale,
    i18nLocale,
    formattingAndLabelRules,
    filterColorData,
  }: ContourSourceDataArgs
): ContourSourceData => {
  const {
    points,
    excludedByDimension,
    excludedCount,
    axisFields,
    listAxes,
    emptyReason,
  } = buildXyzPoints({
    context,
    loadedGraph,
    permissionContext,
    i18nLocale,
    formattingAndLabelRules,
    filterColorData,
    fieldSelection: viewSettings,
    minimumDistinctPositions: CONTOUR_MINIMUM_POSITIONS,
    fieldTypes: FIELD_CHART_FIELD_TYPES,
  });

  return {
    viewInstanceId,
    axisFields,
    xAxisTitle: toFieldLabel(axisFields, viewSettings.selectedFieldNameX),
    yAxisTitle: toFieldLabel(axisFields, viewSettings.selectedFieldNameY),
    valueTitle: toFieldLabel(axisFields, viewSettings.selectedFieldNameZ),
    locale,
    i18nLocale,
    points,
    /* Off the plotted points, which is the population the chart draws. */
    axes: withCategoryAxes(toNiceHorizontalAxes(points), listAxes),
    listAxes,
    listBinCounts: toListBinCounts(points, listAxes),
    excludedByDimension,
    excludedCount,
    isViewEmpty: emptyReason !== null,
    emptyReason,
    coloring: toSurfaceColoring({
      points,
      rules: formattingAndLabelRules.rules,
      filterColorData,
      enhancedScopeData: loadedGraph.scopeData,
    }),
    legend: toFieldColoringLegendData(
      loadedGraph.scopeData,
      formattingAndLabelRules.rules,
      i18nLocale
    ),
  };
};
